import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { database } from "../../database/firebase";
import icon1 from "../../img/icon1.png";
import icon2 from "../../img/icon2.png";
import icon3 from "../../img/icon3.png";
import icon4 from "../../img/icon4.png";
import icon5 from "../../img/icon5.png";
import icon6 from "../../img/icon6.png";
import icon7 from "../../img/icon7.png";
import icon8 from "../../img/icon8.png";
import MonkeyBackdrop from "../../components/MonkeyBackdrop";
import "./waiting.css";

const PLAYER_ICONS = [icon1, icon2, icon3, icon4, icon5, icon6, icon7, icon8];

const LOBBY_COLLECTION = "lobby";
const LOBBY_USERDATA = "userdata";
const GUEST_LOBBY_USER_KEY = "truthLobbyGuestUserId";
const MAX_STATEMENT_LEN = 40;
/** Extra fill time when presenter taps “Add time” (synced via `fillEndsAt`). */
const PRESENTER_FILL_EXTEND_SECONDS = 30;

function hasCompleteStatements(data) {
  const t1 = String(data?.truth1 ?? "").trim();
  const t2 = String(data?.truth2 ?? "").trim();
  const lie = String(data?.lie ?? "").trim();
  return Boolean(t1 && t2 && lie);
}

/**
 * After `status: "holding"` or `"show"`, leave for the Jigsaw app’s `/game` route (or this app’s `/game`).
 * Set `REACT_APP_JIGSAW_GAME_URL` (full URL, e.g. `http://localhost:3001/game`).
 * If unset, stays on this app’s `/game`.
 */
function navigateToPostShowGame(navigate) {
  // const url = String(process.env.REACT_APP_JIGSAW_GAME_URL ?? "").trim();
  // if (url) {
  //   window.location.replace(url);
  //   return;
  // }
  navigate("/game", { replace: true });
}

const FILL_STEPS = [
  {
    field: "truth1",
    title: "What's your first Truth?",
    placeholder: "Something interesting...",
  },
  {
    field: "truth2",
    title: "What's your second Truth?",
    placeholder: "Something interesting...",
  },
  {
    field: "lie",
    title: "What's your Lie?",
    placeholder: "Something believable...",
  },
];

function avatarIconForUserId(userId) {
  let h = 0;
  const s = String(userId);
  for (let i = 0; i < s.length; i += 1) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return PLAYER_ICONS[Math.abs(h) % PLAYER_ICONS.length];
}

function readExplicitCharacterIndex(data) {
  const count = PLAYER_ICONS.length;
  const legacy = String(data?.character ?? "").trim();
  let legacyIndex = null;
  if (legacy) {
    const m = legacy.match(/(\d+)$/);
    if (m) {
      const legacyOneBased = Number(m[1]);
      if (Number.isFinite(legacyOneBased) && legacyOneBased >= 1) {
        legacyIndex = (Math.floor(legacyOneBased) - 1) % count;
      }
    }
  }

  const direct = Number(data?.characterIndex);
  if (Number.isFinite(direct) && direct >= 0) {
    const directIndex = Math.floor(direct) % count;
    if (Number.isFinite(legacyIndex) && legacyIndex >= 0) {
      if (directIndex === legacyIndex) return directIndex;
      if (directIndex - 1 === legacyIndex) return legacyIndex;
      return legacyIndex;
    }
    return directIndex;
  }

  if (Number.isFinite(legacyIndex) && legacyIndex >= 0) {
    return legacyIndex;
  }

  return null;
}

function avatarIconForUser(userId, userData) {
  const max = PLAYER_ICONS.length;
  const idx = readExplicitCharacterIndex(userData);
  if (Number.isFinite(idx) && idx >= 0 && idx < max) {
    return PLAYER_ICONS[Math.floor(idx)];
  }
  return avatarIconForUserId(userId);
}

function readSessionUser() {
  try {
    const raw = sessionStorage.getItem("userData");
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (!u || typeof u !== "object") return null;
    const userId = String(u?.userId ?? u?.userid ?? "").trim();
    return { ...u, userId };
  } catch {
    return null;
  }
}

function readTruthLobby() {
  try {
    const raw = sessionStorage.getItem("truthLobby");
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || typeof o !== "object") return null;
    const sessionId = String(o.sessionId ?? "").trim();
    const lobbyId = String(o.lobbyId ?? "").trim();
    if (!sessionId || !lobbyId) return null;
    return o;
  } catch {
    return null;
  }
}

function resolvePlayerUserId() {
  const u = readSessionUser();
  if (u?.userId) return String(u.userId).trim();
  try {
    const g = sessionStorage.getItem(GUEST_LOBBY_USER_KEY);
    if (g && String(g).trim()) return String(g).trim();
  } catch {
    /* ignore */
  }
  return "";
}

async function writeUserdataLiveFlag(sessionId, roomId, userId, live) {
  const sid = String(sessionId ?? "").trim();
  const rid = String(roomId ?? "").trim();
  const uid = String(userId ?? "").trim();
  if (!sid || !rid || !uid) return;
  try {
    await setDoc(
      doc(
        database,
        LOBBY_COLLECTION,
        sid,
        "rooms",
        rid,
        LOBBY_USERDATA,
        uid
      ),
      { live, liveUpdatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (err) {
    console.warn("waiting userdata live flag:", err);
  }
}

function computeFillSeconds(roomData, lobbyCache) {
  // perPlayerSeconds contains the total seconds selected by presenter
  const totalSeconds = Number(
    roomData?.perPlayerSeconds ?? lobbyCache?.perPlayerSeconds ?? 80
  );
  //return Math.max(60, Number.isFinite(totalSeconds) ? totalSeconds : 80); // Ensure minimum 60 seconds
  return totalSeconds;
}

/**
 * End time for the fill countdown from Firestore (preferred) or a short client fallback.
 */
function resolveFillEndMs(roomData, lobbyCache, localFillStartMs) {
  const fe = roomData?.fillEndsAt;
  if (fe && typeof fe.toMillis === "function") {
    return fe.toMillis();
  }
  const fs = roomData?.fillStartedAt;
  if (fs && typeof fs.toMillis === "function") {
    const d = Number(roomData?.fillDurationSeconds);
    const sec =
      Number.isFinite(d) && d > 0
        ? Math.floor(d)
        : Number(roomData?.perPlayerSeconds ?? lobbyCache?.perPlayerSeconds ?? 80);
    return fs.toMillis() + sec * 1000;
  }
  if (localFillStartMs != null) {
    return localFillStartMs + computeFillSeconds(roomData, lobbyCache) * 1000;
  }
  return null;
}

function resolveFillStartMs(roomData, lobbyCache, localFillStartMs) {
  const fs = roomData?.fillStartedAt;
  if (fs && typeof fs.toMillis === "function") {
    return fs.toMillis();
  }
  const fe = roomData?.fillEndsAt;
  if (fe && typeof fe.toMillis === "function") {
    const totalSec = getTotalFillDurationSec(roomData, lobbyCache);
    if (Number.isFinite(totalSec) && totalSec > 0) {
      return fe.toMillis() - Math.floor(totalSec) * 1000;
    }
  }
  if (localFillStartMs != null) {
    return localFillStartMs;
  }
  return null;
}

/** Full fill budget in seconds (for progress bar = secondsLeft / total). */
function getTotalFillDurationSec(roomData, lobbyCache) {
  // First try to use the explicitly set fillDurationSeconds (calculated by host)
  const d = Number(roomData?.fillDurationSeconds);
  if (Number.isFinite(d) && d > 0) return Math.floor(d);
  
  // Final fallback to computeFillSeconds (should rarely be needed)
  return computeFillSeconds(roomData, lobbyCache);
}

/** Remaining fraction [0,1] for the timer line (prefers Firestore start/end window). */
function getFillLineProgress(
  roomData,
  lobbyCache,
  localFillStartMs,
  secondsLeft,
  timerKnown
) {
  if (!timerKnown) return 1;
  const endMs = resolveFillEndMs(roomData, lobbyCache, localFillStartMs);
  if (endMs == null) return 1;
  const fe = roomData?.fillEndsAt;
  const fs = roomData?.fillStartedAt;
  if (
    fe &&
    typeof fe.toMillis === "function" &&
    fs &&
    typeof fs.toMillis === "function"
  ) {
    const windowMs = Math.max(1, fe.toMillis() - fs.toMillis());
    const remainMs = Math.max(0, endMs - Date.now());
    return Math.max(0, Math.min(1, remainMs / windowMs));
  }
  const totalSec = getTotalFillDurationSec(roomData, lobbyCache);
  if (totalSec <= 0) return 1;
  return Math.max(0, Math.min(1, secondsLeft / totalSec));
}

function HoldTightPanel({ hint, className = "", avatarSrc = "" }) {
  const src = String(avatarSrc ?? "").trim();
  return (
    <div
      className={`waiting__fill-done waiting__fill-done--banner waiting__fill-done--hold${className ? ` ${className}` : ""}`}
    >
      <h2 className="waiting__fill-done-title">Hold tight!</h2>
      {src ? (
        <div className="waiting__fill-hold-icons" aria-hidden>
          <img
            className="waiting__fill-hold-icon waiting__fill-hold-icon--solo"
            src={src}
            alt=""
          />
        </div>
      ) : null}
      <p className="waiting__fill-hint">{hint}</p>
    </div>
  );
}

function Waiting({ updateHeader }) {
  const headerConfiguredRef = useRef(false);
  useEffect(() => {
    if (headerConfiguredRef.current) return;
    headerConfiguredRef.current = true;
    updateHeader({
      backButtonRedirect: "/intro",
      showBackButton: true,
    });
  }, [updateHeader]);
  const navigate = useNavigate();
  const [user, setUser] = useState(() => readSessionUser());
  const [brokenPhoto, setBrokenPhoto] = useState(false);
  const [truthLobby, setTruthLobby] = useState(() => readTruthLobby());
  const [roomStatus, setRoomStatus] = useState("waiting");
  const [roomData, setRoomData] = useState(null);
  const [roomListenError, setRoomListenError] = useState("");
  const fillLocalStartRef = useRef(null);
  const [fillStep, setFillStep] = useState(0);
  const [statements, setStatements] = useState({
    truth1: "",
    truth2: "",
    lie: "",
  });
  const latestStatementsRef = useRef({
    truth1: "",
    truth2: "",
    lie: "",
  });
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [saveState, setSaveState] = useState("idle");
  /** Latch so only one client advances the room when the fill timer hits zero. */
  const fillTimerToHoldingOnceRef = useRef(false);
  /** Latch when presenter moves room to holding after everyone submitted. */
  const allSubmittedToHoldingOnceRef = useRef(false);
  const [presenterBusy, setPresenterBusy] = useState(false);
  const [presenterError, setPresenterError] = useState("");
  const [submittedCount, setSubmittedCount] = useState(0);
  /** How many userdata docs exist in this room (fallback when maxPlayers is unset). */
  const [userdataDocCount, setUserdataDocCount] = useState(0);
  const [statementSubmit, setStatementSubmit] = useState(false);
  const [currentUserRoomData, setCurrentUserRoomData] = useState(null);
  const [timeAdded, setTimeAdded] = useState(0);
  const [showEndGameConfirm, setShowEndGameConfirm] = useState(false);

  useEffect(() => {
    setUser(readSessionUser());
    setTruthLobby(readTruthLobby());
  }, []);

  /**
   * `lobby/{sessionId}/rooms/{roomId}/userdata/{userId}` — `live: true` while this
   * page is open; `live: false` on React unmount (navigate away) or `pagehide`
   * (tab close / browser exit). Skip `persisted` pages (bfcache restore).
   */
  useEffect(() => {
    const sessionId = String(truthLobby?.sessionId ?? "").trim();
    const lobbyId = String(truthLobby?.lobbyId ?? "").trim();
    const userId = resolvePlayerUserId();
    const target = { sessionId, lobbyId, userId };

    const clearLive = () => {
      void writeUserdataLiveFlag(
        target.sessionId,
        target.lobbyId,
        target.userId,
        false
      );
    };

    if (!sessionId || !lobbyId || !userId) {
      return undefined;
    }

    void writeUserdataLiveFlag(sessionId, lobbyId, userId, true);

    const onPageHide = (e) => {
      if (e.persisted === true) return;
      clearLive();
    };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      clearLive();
    };
  }, [truthLobby?.sessionId, truthLobby?.lobbyId, user?.userId]);

  useEffect(() => {
    const lobby = readTruthLobby();
    if (!lobby?.sessionId || !lobby?.lobbyId) {
      setRoomListenError("");
      setRoomData(null);
      setRoomStatus("waiting");
      return undefined;
    }
    const ref = doc(
      database,
      LOBBY_COLLECTION,
      lobby.sessionId,
      "rooms",
      lobby.lobbyId
    );
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setRoomListenError("");
        if (!snap.exists()) {
          setRoomData(null);
          setRoomStatus("waiting");
          return;
        }
        const data = snap.data();
        setRoomData(data);
        const st = String(data?.status ?? "waiting").trim().toLowerCase();
        setRoomStatus(st || "waiting");
      },
      (err) => {
        console.error("waiting room snapshot:", err);
        setRoomListenError(err?.message || "Could not load room.");
      }
    );
    return () => unsub();
  }, [truthLobby?.sessionId, truthLobby?.lobbyId]);

  useEffect(() => {
    const lobby = readTruthLobby();
    if (!lobby?.sessionId || !lobby?.lobbyId) {
      setSubmittedCount(0);
      setUserdataDocCount(0);
      setStatementSubmit(false);
      return undefined;
    }
    const ref = collection(
      database,
      LOBBY_COLLECTION,
      lobby.sessionId,
      "rooms",
      lobby.lobbyId,
      LOBBY_USERDATA
    );
    const unsub = onSnapshot(
      ref,
      (snap) => {
        let submittedWithStatements = 0;
        let docs = 0;
        let currentUserSubmitted = false;
        const currentUserId = resolvePlayerUserId();
        let currentUserData = null;

        snap.forEach((d) => {
          docs += 1;
          const data = d.data() || {};
          if (data.statementsSubmitted && hasCompleteStatements(data)) {
            submittedWithStatements += 1;
          }

          if (d.id === currentUserId) {
            currentUserData = data;
            if (data.statementsSubmitted) {
              currentUserSubmitted = true;
            }
          }
        });

        setSubmittedCount(submittedWithStatements);
        setUserdataDocCount(docs);
        setStatementSubmit(currentUserSubmitted);
        setCurrentUserRoomData(currentUserData);
      },
      () => {
        setSubmittedCount(0);
        setUserdataDocCount(0);
        setStatementSubmit(false);
        setCurrentUserRoomData(null);
      }
    );
    return () => unsub();
  }, [truthLobby?.sessionId, truthLobby?.lobbyId]);

  /** When the room enters the play phase (`holding` / `show`), go to the game screen. */
  useEffect(() => {
    const sessionId = String(truthLobby?.sessionId ?? "").trim();
    const lobbyId = String(truthLobby?.lobbyId ?? "").trim();
    if (!sessionId || !lobbyId) return;
    if (roomStatus !== "holding" && roomStatus !== "show") return;
    navigateToPostShowGame(navigate);
  }, [roomStatus, truthLobby?.sessionId, truthLobby?.lobbyId, navigate]);

  useEffect(() => {
    if (roomStatus !== "fill") {
      fillLocalStartRef.current = null;
      setFillStep(0);
      setStatements({ truth1: "", truth2: "", lie: "" });
      latestStatementsRef.current = { truth1: "", truth2: "", lie: "" };
      setSaveState("idle");
      fillTimerToHoldingOnceRef.current = false;
      allSubmittedToHoldingOnceRef.current = false;
      return;
    }
    const hasServerTimer =
      (roomData?.fillEndsAt &&
        typeof roomData.fillEndsAt.toMillis === "function") ||
      (roomData?.fillStartedAt &&
        typeof roomData.fillStartedAt.toMillis === "function");
    if (hasServerTimer) {
      fillLocalStartRef.current = null;
      return;
    }
    if (fillLocalStartRef.current == null) {
      fillLocalStartRef.current = Date.now();
    }
  }, [roomStatus, roomData]);

  const lobbyCache = truthLobby;

  /** Presenter / host controls: never for users who joined as Player (`gameMode`). */
  const isPresenterHost = useMemo(() => {
    const gm = String(truthLobby?.gameMode ?? "").trim().toLowerCase();
    if (gm === "player") return false;
    const me = resolvePlayerUserId();
    const pid = String(
      roomData?.presenterUserId ?? truthLobby?.presenterUserId ?? ""
    ).trim();
    return Boolean(me && pid && pid === me);
  }, [
    roomData?.presenterUserId,
    truthLobby?.presenterUserId,
    truthLobby?.gameMode,
    user?.userId,
    truthLobby?.sessionId,
    truthLobby?.lobbyId,
  ]);

  const resolveLobbyPath = useCallback(() => {
    const fromStorage = readTruthLobby();
    const lobby =
      truthLobby?.sessionId && truthLobby?.lobbyId ? truthLobby : fromStorage;
    const sessionId = String(lobby?.sessionId ?? "").trim();
    const roomId = String(lobby?.lobbyId ?? "").trim();
    return { sessionId, roomId };
  }, [truthLobby?.sessionId, truthLobby?.lobbyId]);

  const getFillEndMs = useCallback(() => {
    const end = resolveFillEndMs(
      roomData,
      lobbyCache,
      fillLocalStartRef.current
    );
    if (end != null) return end;
    return Date.now() + 60_000;
  }, [roomData, lobbyCache]);

  const handlePresenterExtendTimer = useCallback(async () => {
    if (!isPresenterHost || presenterBusy) return;
    const { sessionId, roomId } = resolveLobbyPath();
    if (!sessionId || !roomId) return;
    setPresenterError("");
    setPresenterBusy(true);
    try {
      const now = Date.now();
      const currentEnd = Math.max(getFillEndMs(), now);
      const extensionAmount = Math.max(15, PRESENTER_FILL_EXTEND_SECONDS);
      const newEndMs = currentEnd + extensionAmount * 1000;
      await setDoc(
        doc(database, LOBBY_COLLECTION, sessionId, "rooms", roomId),
        {
          fillEndsAt: Timestamp.fromMillis(newEndMs),
          fillTimerExtendedAt: serverTimestamp(),
        },
        { merge: true }
      );
      // Show visual feedback
      setTimeAdded(extensionAmount);
      setTimeout(() => setTimeAdded(0), 2000);
    } catch (e) {
      console.error("waiting extend fill timer:", e);
      setPresenterError(
        String(e?.message || "").trim() ||
          "Could not extend timer. Check Firestore rules."
      );
    } finally {
      setPresenterBusy(false);
    }
  }, [
    getFillEndMs,
    isPresenterHost,
    presenterBusy,
    resolveLobbyPath,
  ]);

  const handlePresenterNextRound = useCallback(async () => {
    if (!isPresenterHost || presenterBusy) return;
    setShowEndGameConfirm(true);
  }, [isPresenterHost, presenterBusy]);

  const confirmEndGame = useCallback(async () => {
    setShowEndGameConfirm(false);
    const { sessionId, roomId } = resolveLobbyPath();
    if (!sessionId || !roomId) return;
    setPresenterError("");
    setPresenterBusy(true);
    try {
      await setDoc(
        doc(database, LOBBY_COLLECTION, sessionId, "rooms", roomId),
        {
          status: "holding",
          statusUpdatedAt: serverTimestamp(),
          fillEndedByPresenterAt: serverTimestamp(),
        },
        { merge: true }
      );
      // Redirect to game after setting status
      navigateToPostShowGame(navigate);
    } catch (e) {
      console.error("waiting end game setup:", e);
      setPresenterError(
        String(e?.message || "").trim() ||
          "Could not end setup. Check Firestore rules."
      );
    } finally {
      setPresenterBusy(false);
    }
  }, [resolveLobbyPath, navigate]);

  useEffect(() => {
    if (roomStatus !== "fill") setPresenterError("");
  }, [roomStatus]);

  useEffect(() => {
    if (roomStatus !== "fill") return undefined;

    const tick = () => {
      const endMs = resolveFillEndMs(
        roomData,
        lobbyCache,
        fillLocalStartRef.current
      );
      if (endMs == null) {
        return;
      }
      setSecondsLeft(Math.max(0, Math.ceil((endMs - Date.now()) / 1000)));
    };

    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [roomStatus, roomData, lobbyCache]);

  const persistStatements = useCallback(
    async (opts = {}) => {
      const { silent } = opts;
      const fromStorage = readTruthLobby();
      const lobby =
        truthLobby?.sessionId && truthLobby?.lobbyId ? truthLobby : fromStorage;
      const sessionId = String(lobby?.sessionId ?? "").trim();
      const roomId = String(lobby?.lobbyId ?? "").trim();
      const userId = resolvePlayerUserId();
      if (!sessionId || !roomId || !userId) {
        if (!silent) setSaveState("error");
        return false;
      }
      if (!silent) setSaveState("saving");
      const latest = latestStatementsRef.current || {};
      const t1 = String(latest.truth1 ?? "").slice(0, MAX_STATEMENT_LEN);
      const t2 = String(latest.truth2 ?? "").slice(0, MAX_STATEMENT_LEN);
      const lie = String(latest.lie ?? "").slice(0, MAX_STATEMENT_LEN);
      const payload = {
        truth1: t1,
        truth2: t2,
        lie,
        statementsUpdatedAt: serverTimestamp(),
      };
      if (!silent) {
        payload.statementsSubmitted = true;
        payload.statementsSubmittedAt = serverTimestamp();
      }
      try {
        await setDoc(
          doc(
            database,
            LOBBY_COLLECTION,
            sessionId,
            "rooms",
            roomId,
            LOBBY_USERDATA,
            userId
          ),
          payload,
          { merge: true }
        );
        if (!silent) {
          setSaveState("saved");
        }
        return true;
      } catch (e) {
        console.error("waiting persist statements:", e);
        if (!silent) setSaveState("error");
        return false;
      }
    },
    [truthLobby?.sessionId, truthLobby?.lobbyId]
  );

  /**
   * When the fill countdown hits zero: merge-save any typed statements to userdata,
   * then the presenter updates the room to `holding` (other clients rely on snapshot + navigate effect).
   */
  useEffect(() => {
    if (roomStatus !== "fill" || secondsLeft > 0 || fillTimerToHoldingOnceRef.current)
      return;

    const endMs = resolveFillEndMs(
      roomData,
      lobbyCache,
      fillLocalStartRef.current
    );
    if (endMs == null) return;
    if (Date.now() < endMs) return;

    fillTimerToHoldingOnceRef.current = true;
    void (async () => {
      const latest = latestStatementsRef.current || {};
      const hasTypedAnything =
        String(latest.truth1 ?? "").trim().length > 0 ||
        String(latest.truth2 ?? "").trim().length > 0 ||
        String(latest.lie ?? "").trim().length > 0;

      if (hasTypedAnything) {
        const complete = hasCompleteStatements(latest);
        const savedOk = complete
          ? await persistStatements()
          : await persistStatements({ silent: true });
        if (!savedOk) {
          fillTimerToHoldingOnceRef.current = false;
          return;
        }
        if (!complete) {
          setSaveState("timeoutPartial");
        }
      }

      const lobby = readTruthLobby();
      const sessionId = String(lobby?.sessionId ?? "").trim();
      const roomId = String(lobby?.lobbyId ?? "").trim();
      if (!sessionId || !roomId) {
        fillTimerToHoldingOnceRef.current = false;
        return;
      }
      if (!isPresenterHost) return;
      try {
        await setDoc(
          doc(database, LOBBY_COLLECTION, sessionId, "rooms", roomId),
          {
            status: "holding",
            statusUpdatedAt: serverTimestamp(),
            fillEndedAtTimer: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (e) {
        console.error("waiting fill timer -> holding:", e);
        fillTimerToHoldingOnceRef.current = false;
      }
    })();
  }, [
    roomStatus,
    secondsLeft,
    roomData,
    lobbyCache,
    isPresenterHost,
    persistStatements,
  ]);

  const displayName = useMemo(() => {
    const u = user;
    if (!u) return "Player";
    return (
      String(u.name ?? "").trim() ||
      String(u.email ?? "").trim() ||
      String(u.userId ?? "").trim() ||
      "Player"
    );
  }, [user]);

  const photoURL = useMemo(() => {
    const u = user;
    if (!u) return "";
    const p =
      u.photoURL || u.photoUrl || u.avatarUrl || u.avatar || u.picture || "";
    return typeof p === "string" ? p.trim() : "";
  }, [user]);

  const fallbackIcon = useMemo(() => {
    const id = user?.userId || resolvePlayerUserId() || "guest";
    return avatarIconForUser(id, currentUserRoomData || user);
  }, [currentUserRoomData, user]);

  /** Same face as the lobby avatar: profile photo or assigned character icon. */
  const holdTightAvatarSrc = useMemo(() => {
    if (photoURL && !brokenPhoto) return photoURL;
    return fallbackIcon;
  }, [photoURL, brokenPhoto, fallbackIcon]);

  const isFill = roomStatus === "fill";
  const expectedPlayers = useMemo(() => {
    const n = Number(roomData?.maxPlayers ?? lobbyCache?.maxPlayers ?? 0);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
    return 0;
  }, [roomData?.maxPlayers, lobbyCache?.maxPlayers]);
  const effectiveExpectedSubmitters =
    expectedPlayers > 0 ? expectedPlayers : userdataDocCount;
  const allStatementsSubmittedInTime =
    isFill &&
    effectiveExpectedSubmitters > 0 &&
    submittedCount >= effectiveExpectedSubmitters;
  const showSubmittedHoldingInFill =
    isFill && statementSubmit;
  const fillEndResolvedMs = resolveFillEndMs(
    roomData,
    lobbyCache,
    fillLocalStartRef.current
  );
  const timerKnown = fillEndResolvedMs != null;

  /** Total seconds left — badge display (reference UI). */
  const timerSecondsBadge = timerKnown ? String(secondsLeft) : "—";
  const fillLineProgress = getFillLineProgress(
    roomData,
    lobbyCache,
    fillLocalStartRef.current,
    secondsLeft,
    timerKnown
  );
  /** Blocks further step/submit after success or partial timeout autosave. */
  const statementsSubmitted = saveState === "saved";
  const stepDef = FILL_STEPS[fillStep] ?? FILL_STEPS[0];
  const currentValue = statements[stepDef.field] ?? "";
  const canAdvance =
    currentValue.trim().length > 0 && currentValue.length <= MAX_STATEMENT_LEN;
  const isLastStep = fillStep >= FILL_STEPS.length - 1;

  const onChangeStatement = (e) => {
    const v = e.target.value.slice(0, MAX_STATEMENT_LEN);
    setStatements((prev) => {
      const next = { ...prev, [stepDef.field]: v };
      latestStatementsRef.current = next;
      return next;
    });
  };

  const handleNext = async () => {
    if (
      statementsSubmitted ||
      saveState === "timeoutPartial" ||
      saveState === "saving" ||
      !canAdvance ||
      !timerKnown ||
      secondsLeft <= 0
    )
      return;
    if (!isLastStep) {
      await persistStatements({ silent: true });
      setFillStep((s) => s + 1);
      return;
    }
    const ok = await persistStatements();
    if (!ok) return;
  };

  const missingLobby = !truthLobby?.sessionId || !truthLobby?.lobbyId;

  useEffect(() => {
    if (!allStatementsSubmittedInTime || allSubmittedToHoldingOnceRef.current)
      return;
    if (!isPresenterHost) return;
    allSubmittedToHoldingOnceRef.current = true;
    void (async () => {
      const lobby = readTruthLobby();
      const sessionId = String(lobby?.sessionId ?? "").trim();
      const roomId = String(lobby?.lobbyId ?? "").trim();
      if (!sessionId || !roomId) {
        allSubmittedToHoldingOnceRef.current = false;
        return;
      }
      try {
        await setDoc(
          doc(database, LOBBY_COLLECTION, sessionId, "rooms", roomId),
          {
            status: "holding",
            statusUpdatedAt: serverTimestamp(),
            fillEndedAllSubmittedAt: serverTimestamp(),
          },
          { merge: true }
        );
        // Redirect to game after setting status
        navigateToPostShowGame(navigate);
      } catch (e) {
        console.error("waiting all statements -> holding:", e);
        allSubmittedToHoldingOnceRef.current = false;
      }
    })();
  }, [allStatementsSubmittedInTime, isPresenterHost, navigate]);

  return (
    <div
      className={`waiting${isFill ? " waiting--fill" : ""}${
        isFill && isPresenterHost ? " waiting--fill-with-presenter-dock" : ""
      }`}
    >
      <MonkeyBackdrop />
      <div className="waiting__bg-glow" aria-hidden />
      <div className="waiting__bg-texture" aria-hidden />

      {missingLobby ? (
        <div className="waiting__main">
          <p className="waiting__subtitle">No active lobby found.</p>
          <p className="waiting__fill-hint">Open a lobby from the game page, then try again.</p>
          <button type="button" className="waiting__fill-next" onClick={() => navigate("/userpage")}>
            Go to lobby
          </button>
        </div>
      ) : isFill ? (
        <div className="waiting__fill">
          <div className="waiting__fill-top">
            <div className="waiting__fill-timer-wrap">
              <div className="waiting__fill-timer-line-track" aria-hidden>
                <div
                  className="waiting__fill-timer-line-progress"
                  style={{
                    transform: `scaleX(${fillLineProgress})`,
                  }}
                />
              </div>
              <div
                className={`waiting__fill-timer-badge${
                  timerKnown && secondsLeft <= 30
                    ? " waiting__fill-timer-badge--warn"
                    : ""
                }`}
                role="timer"
                aria-live="polite"
                aria-label={
                  timerKnown
                    ? `Seconds remaining: ${secondsLeft}`
                    : "Timer loading"
                }
              >
                {timerSecondsBadge}
              </div>
            </div>
          </div>
          {!timerKnown ? (
            <p className="waiting__fill-hint" role="status">
              Syncing timer from the host…
            </p>
          ) : null}

          {roomListenError ? (
            <p className="waiting__fill-error" role="alert">
              {roomListenError}
            </p>
          ) : null}

          {showSubmittedHoldingInFill && (
            <HoldTightPanel
              avatarSrc={holdTightAvatarSrc}
              hint="Your statements are saved. When the host ends the round, you'll head to the game together."
            />
          )}
          {!showSubmittedHoldingInFill ? (
            <>
              <h2 className="waiting__fill-prompt">{stepDef.title}</h2>
              <label className="waiting__fill-label" htmlFor="waiting-fill-input">
                <span className="waiting__visually-hidden">{stepDef.title}</span>
                <textarea
                  id="waiting-fill-input"
                  className="waiting__fill-textarea"
                  value={currentValue}
                  onChange={onChangeStatement}
                  placeholder={stepDef.placeholder}
                  maxLength={MAX_STATEMENT_LEN}
                  rows={5}
                  disabled={!timerKnown || secondsLeft <= 0}
                />
              </label>
              <div className="waiting__fill-counter" aria-live="polite">
                {currentValue.length} / {MAX_STATEMENT_LEN}
              </div>
              {saveState === "error" ? (
                <p className="waiting__fill-error" role="alert">
                  Could not save. Check your connection and try again.
                </p>
              ) : null}
              <button
                type="button"
                className="waiting__fill-next"
                onClick={handleNext}
                disabled={
                  !canAdvance ||
                  !timerKnown ||
                  secondsLeft <= 0 ||
                  saveState === "saving"
                }
              >
                {saveState === "saving"
                  ? "Saving…"
                  : isLastStep
                    ? "Submit"
                    : "Next"}
              </button>
            </>
          ) : null}
          {timerKnown &&
          secondsLeft <= 0 &&
          saveState !== "saved" &&
          saveState !== "saving" ? (
            <p className="waiting__fill-hint" role="status">
              {fillLocalStartRef.current && (Date.now() - fillLocalStartRef.current) < 10000
                ? "You joined after time expired. Waiting for the host to continue..."
                : saveState === "timeoutPartial"
                  ? "Time's up — your progress was saved."
                  : "Time's up — waiting for the host to continue the game."}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="waiting__main">
          <h1 className="waiting__title">
            <span className="waiting__title-text">Truth &amp; Trick</span>
            <span className="waiting__title-icon" aria-hidden="true">
              👁️
            </span>
          </h1>
          {roomStatus === "holding" || roomStatus === "show" ? null : (
            <p className="waiting__subtitle">
              {roomStatus === "waiting"
                ? "Waiting for the host to start…"
                : "The game will begin shortly!"}
            </p>
          )}
          {roomStatus === "holding" || roomStatus === "show" ? (
            <HoldTightPanel
              className="waiting__hold-tight--status-show"
              avatarSrc={holdTightAvatarSrc}
              hint="The round is open — you're heading to the game."
            />
          ) : null}
          {roomListenError ? (
            <p className="waiting__fill-error" role="alert">
              {roomListenError}
            </p>
          ) : null}
          <div className="waiting__avatar-wrap">
            {photoURL && !brokenPhoto ? (
              <img
                className="waiting__avatar waiting__avatar--bob"
                src={photoURL}
                alt=""
                onError={() => setBrokenPhoto(true)}
              />
            ) : (
              <img
                className="waiting__avatar waiting__avatar--fallback waiting__avatar--character waiting__avatar--bob"
                src={fallbackIcon}
                alt=""
              />
            )}
          </div>
          <p className="waiting__username waiting__username--motion">{displayName}</p>
        </div>
      )}

      {isFill && isPresenterHost ? (
        <div className="waiting__presenter-dock" aria-label="Presenter controls">
          <div className="waiting__presenter-dock-inner">
            <button
              type="button"
              className="waiting__presenter-dock-btn"
              onClick={handlePresenterExtendTimer}
              disabled={presenterBusy}
            >
              Increase timer {presenterBusy ? "Wait…" : timeAdded > 0 ? `+${timeAdded}s Added!` : `+${PRESENTER_FILL_EXTEND_SECONDS}s`}
            </button>
            <button
              type="button"
              className="waiting__presenter-dock-btn waiting__presenter-dock-btn--end"
              onClick={handlePresenterNextRound}
              disabled={presenterBusy}
            >
              {presenterBusy ? "Wait…" : " Continue to Game"}
            </button>
          </div>
          {presenterError ? (
            <p className="waiting__presenter-dock-error" role="alert">
              {presenterError}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Custom Confirmation Popup */}
      {showEndGameConfirm && (
        <div className="waiting__confirm-overlay">
          <div className="waiting__confirm-popup">
            <div className="waiting__confirm-content">
              <h3 className="waiting__confirm-title">End Game Setup</h3>
              <p className="waiting__confirm-message">
                End game setup now? The room will move to the game phase and everyone will go to the game screen.
              </p>
              <div className="waiting__confirm-actions">
                <button
                  type="button"
                  className="waiting__confirm-btn waiting__confirm-btn--cancel"
                  onClick={() => setShowEndGameConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="waiting__confirm-btn waiting__confirm-btn--confirm"
                  onClick={confirmEndGame}
                >
                  Continue to Game
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Waiting;
